'use es6';

import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { INPAGE_SIDEBAR_IFRAME_ROOT_DIV_ID } from 'ContentEditorUI/lib/app/constants';
import { NAMESPACE_CLASS } from 'ContentEditorUI/lib/inpage/Constants';
import { __PRIVATE__ as StyledComponentsInternals } from 'styled-components';
import namespacePlugin from 'foundations-theming/stylisPlugins/namespacePlugin';
import { KeyboardModeProvider, GlobalCSSVariables } from 'foundations-components/components/AppProvider';
import { ThemeProvider } from 'foundations-theming/components/ThemeProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class InpageSidebarPortal extends Component {
  constructor(props) {
    super(props);

    // Can do this in the constructor, since we now the necessary DOM exists and we are only
    // using this component once
    this.prepPortalDom();
    const {
      sidebarIframeWindow
    } = props;
    const {
      StyleSheet
    } = StyledComponentsInternals;
    const sidebarIframeHead = sidebarIframeWindow.document.head;
    // Context: https://git.hubteam.com/HubSpot/ContentEditorUI/pull/16382
    // TLDR of ^ is that our current version of `styled-components` (npm version 5.1.0) has a bug in the
    // `StyleSheetManager` where any styles already rendered in the preview frame won't be applied
    // to the sidebar iframe. This is a problem because the sidebar iframe may not be ready by the time this
    // component is rendered, giving the preview frame the chance to render `UIComponents` before portal
    // rendering the sidebar iframe. As a result of this bug, any `UIComponents` rendered in the preview with
    // the same css hash won't be applied to the sidebar iframe. The fix here is to supply the `StyleSheetManager`
    // with a brand new `StyleSheet`. The problem though is that the `StyleSheet` is tucked away in a `__PRIVATE__` export
    // which indicates to me that it may not be stable. Fortunately, the fix for the bug came in npm version 5.2.0 so
    // our next bender repackage should include the fix.
    // UPON UPGRADE - Please check the npm version of `styled-components` and verify that it includes https://github.com/styled-components/styled-components/pull/3159
    // Then, basically revert that CEUI PR above and use supply the `StyleSheetManager` with a `target `of the sidebar iframe's head
    // and validate that the issue in the PR doesn't occur
    this.stylesheet = new StyleSheet({
      target: sidebarIframeHead
    });
  }
  componentDidMount() {
    const {
      sidebarIframeWindow
    } = this.props;

    // Manually reset body margin that would usually come from app css, but isn't a styled
    // component so isn't copied over
    sidebarIframeWindow.document.body.style.margin = 0;

    // Setup focus handlers for sidebar iframe
    this.sidebarBodyElement = sidebarIframeWindow.document.body;
  }
  prepPortalDom() {
    const {
      sidebarIframeWindow
    } = this.props;
    this.sidebarRootElement = sidebarIframeWindow.document.getElementById(INPAGE_SIDEBAR_IFRAME_ROOT_DIV_ID);

    // Remove any existing DOM nodes off the body of the sidebar iframe (this happens when the preview iframe renders
    // into this frame, then the preview iframe refreshes, leaving the nodes it rendered into the sidebar iframe
    // abandoned). This "cleans up" the tree nodes left over so we can render a fresh tree into the sidebar.
    while (this.sidebarRootElement.firstChild) {
      this.sidebarRootElement.removeChild(this.sidebarRootElement.firstChild);
    }
    sidebarIframeWindow.document.documentElement.classList.add(NAMESPACE_CLASS);
  }
  render() {
    const {
      children,
      isTrellisTheme
    } = this.props;
    const themeName = isTrellisTheme ? 'trellis' : 'canvas';
    const themeMode = 'light';
    // Using StyleSheetManager from styled-components to inject the css generated by styled-components
    // into the sidebar iframe's head, instead of into the inpage iframe
    // See docs: https://styled-components.com/docs/api#stylesheetmanager
    const styleSheetManagerProps = {
      sheet: this.stylesheet,
      stylisPlugins: [namespacePlugin('.hs-private.hs-private.hs-private')]
    };
    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(ThemeProvider, {
        themeName: themeName,
        themeMode: themeMode,
        DO_NOT_USE_stylesheetManagerProps: styleSheetManagerProps,
        children: /*#__PURE__*/_jsxs(KeyboardModeProvider, {
          targetElement: this.sidebarBodyElement,
          children: [/*#__PURE__*/_jsx(GlobalCSSVariables, {}), children]
        })
      }), this.sidebarRootElement)
    });
  }
}
InpageSidebarPortal.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarIframeWindow: PropTypes.object,
  isTrellisTheme: PropTypes.bool
};
export default InpageSidebarPortal;